import {
  type AdminDomainSelectDto,
  type AdminDomainUpdateDto,
  type AdminDomainsSelectDto,
  AdminDomainsSortField,
  AdminDomainsSortOrder,
  AdminDomainsTypeFilter,
} from '@@types/api.types';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { queryKeys } from '@queries/keys';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetCurrentUser } from './users.query';

export type DomainsQueryParams = {
  limit: number;
  order: AdminDomainsSortOrder;
  page: number;
  search?: string;
  sort: AdminDomainsSortField;
  type: AdminDomainsTypeFilter;
  withoutSubdomains?: boolean;
};

export const useAdminDomainsQuery = (params: DomainsQueryParams) => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  return useQuery<AdminDomainsSelectDto, ApiErrorWrapper>({
    enabled: isAdmin,
    queryFn: async () => {
      return await apiGet<AdminDomainsSelectDto>({
        path: '/v1/admin/domains',
        query: params,
      });
    },
    queryKey: queryKeys.adminDomains.getDomains(params),
    placeholderData: keepPreviousData,
  });
};

export const useAdminDomainQuery = (id: string | undefined, preloadedData?: AdminDomainSelectDto) => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  const enabled = isAdmin && !!id;

  return useQuery<AdminDomainSelectDto, ApiErrorWrapper>({
    enabled,
    initialData: preloadedData,
    queryFn: async () => {
      return await apiGet<AdminDomainSelectDto>({
        path: `/v1/admin/domains/${id}`,
      });
    },
    queryKey: queryKeys.adminDomains.getDomainById(id ?? ''),
  });
};

export const useAdminUpdateDomain = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminDomainSelectDto, ApiErrorWrapper, { dto: AdminDomainUpdateDto; id: string }>({
    mutationFn: async (params) => {
      const { dto, id } = params;
      return await apiPatch<AdminDomainSelectDto>({
        body: dto,
        path: `/v1/admin/domains/${id}`,
      });
    },
    onSuccess: (data) => {
      void queryClient.setQueryData(queryKeys.adminDomains.getDomainById(data.id), data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminDomains.root });
    },
  });
};

export const useAdminUpdateDomainStatus = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminDomainSelectDto, ApiErrorWrapper, { archived: boolean; id: string }>({
    mutationFn: async (params) => {
      const { archived, id } = params;
      return await apiPatch<AdminDomainSelectDto>({
        body: { archived },
        path: `/v1/admin/domains/${id}/visibility`,
      });
    },
    onSuccess: (data) => {
      void queryClient.setQueryData(queryKeys.adminDomains.getDomainById(data.id), data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminDomains.root });
    },
  });
};

export const useAdminDeleteDomain = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, ApiErrorWrapper, string>({
    mutationFn: async (id) => {
      return await apiDelete<{ success: boolean }>({
        path: `/v1/admin/domains/${id}`,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminDomains.getDomains() });
    },
  });
};

export const useAdminCreateDomain = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminDomainSelectDto, ApiErrorWrapper, AdminDomainUpdateDto>({
    mutationFn: async (dto) => {
      return await apiPost<AdminDomainSelectDto>({
        body: dto,
        path: `/v1/admin/domains`,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminDomains.root });
    },
  });
};

export const useAdminAvailableParentDomainsQuery = (domainId?: string) => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  return useQuery<AdminDomainsSelectDto, ApiErrorWrapper>({
    enabled: isAdmin,
    queryFn: async () => {
      return await apiGet<AdminDomainsSelectDto>({
        path: '/v1/admin/domains',
        query: {
          limit: 100,
          order: AdminDomainsSortOrder.ASC,
          page: 1,
          sort: AdminDomainsSortField.NAME,
          type: AdminDomainsTypeFilter.MAIN,
        },
      });
    },
    queryKey: queryKeys.adminDomains.getAvailableParents(domainId),
  });
};

export const useAdminAvailableSubdomainsQuery = () => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  return useQuery<AdminDomainsSelectDto, ApiErrorWrapper>({
    enabled: isAdmin,
    queryFn: async () => {
      return await apiGet<AdminDomainsSelectDto>({
        path: '/v1/admin/domains',
        query: {
          limit: 100,
          order: AdminDomainsSortOrder.ASC,
          page: 1,
          sort: AdminDomainsSortField.NAME,
          type: AdminDomainsTypeFilter.MAIN,
          withoutSubdomains: true,
        },
      });
    },
    queryKey: queryKeys.adminDomains.getAvailableSubdomains(),
  });
};
